import React, { type FC, useMemo, useState } from 'react';
import {
  DateLabel,
  DateLabelContainer,
  IconContainer,
  MainTrainingCard,
  ProgressBar,
  ProgressBarContainer,
  ProgressFill,
  Row,
  Title,
  TitleContainer,
} from '../TrainingCard';
import styled, { useTheme } from 'styled-components';
import { useLanguageContext } from '../../../../lib/hooks/useLanguageContext';
import { Icon, IconType } from '../../../Props/Icon/Icon';
import { CardStatus } from '../../enums/CardStatus';
import { getCardStatus } from '../../utils/getCardStatus';
import { TrainingCompletionModal } from '../TrainingCompletionModal';
import { NamedContentEntryText } from '../../../ContentEntryText/NamedContentEntryText';
import { Tooltip } from '../../../Tooltip';
import { NudgeModal, type NudgeModalConfigs } from '../../../Nudge/NudgeModal';
import { useCampaignSendNudgeMutation } from '../../../../apiHooks/useCampaignSendNudgeMutation';
import toast from 'react-hot-toast';
import { useNamedContentEntryText } from '../../../../lib/hooks/contentEntry/useNamedContentEntryText';

type ManagerTeamTrainingCardProps = {
  title: string;
  dueDate: string;
  totalLearners: number;
  learnersCompletedTotal: number;
  estimatedCompletionTime: number;
  nudgeActionText: React.ReactNode;
  campaignId: string;
  hideTooltip?: boolean;
};

export const ManagerTeamTrainingCard: FC<ManagerTeamTrainingCardProps> = ({
  title,
  dueDate,
  totalLearners,
  learnersCompletedTotal,
  estimatedCompletionTime,
  campaignId,
  hideTooltip = false,
}: ManagerTeamTrainingCardProps) => {
  const dd = useMemo(() => new Date(dueDate), [dueDate]);
  const cardStatus: CardStatus = getCardStatus(dd);
  const progress = totalLearners > 0 ? (learnersCompletedTotal / totalLearners) * 100 : 0;
  const isCompleted = learnersCompletedTotal === totalLearners;
  const [langCode] = useLanguageContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nudgeModalConfigs, setNudgeModalConfigs] = useState<NudgeModalConfigs>({
    isOpen: false,
  });
  const successToastText = useNamedContentEntryText({
    refKey: 'managerTrainingNudgeDialog',
    subKey: 'nudgeSentSuccessfully',
  });
  const failToastText = useNamedContentEntryText({
    refKey: 'managerTrainingNudgeDialog',
    subKey: 'nudgeFailure',
  });
  const { mutate, isPending } = useCampaignSendNudgeMutation();

  const handleSendNudge = () => {
    mutate(
      {
        campaignId,
      },
      {
        onSuccess: () => {
          toast(
            (t) => (
              <NudgeToast>
                <Icon icon={IconType.CHECK_CIRCLE} width={20} height={20} fill="transparent" />
                <span>{successToastText}</span>
                <Icon onClick={() => toast.dismiss(t.id)} icon={IconType.TIMES} width={24} height={24} />
              </NudgeToast>
            ),
            {
              position: 'bottom-center',
              style: {
                background: theme.colors.prototype.foreground,
                color: theme.colors.prototype.primaryForeground,
                padding: '8px 0',
              },
            },
          );
          setNudgeModalConfigs({
            isOpen: false,
          });
        },
        onError: () => {
          toast(
            (t) => (
              <NudgeToast>
                <Icon
                  className="alert-triangle"
                  icon={IconType.ALERT_TRIANGLE}
                  width={20}
                  height={20}
                  fill="transparent"
                />
                <span>{failToastText}</span>
                <Icon onClick={() => toast.dismiss(t.id)} icon={IconType.TIMES} width={24} height={24} />
              </NudgeToast>
            ),
            {
              position: 'bottom-center',
              style: {
                background: theme.colors.prototype.foreground,
                color: theme.colors.prototype.primaryForeground,
                padding: '8px 0',
              },
            },
          );
          setNudgeModalConfigs({
            isOpen: false,
          });
        },
      },
    );
  };

  const handleOpenTrainingUsersModal = () => {
    if (!isModalOpen) {
      setIsModalOpen(true);
    }
  };

  const theme = useTheme();

  return (
    <>
      <MainTrainingCard cardStatus={cardStatus}>
        <Row>
          <TitleContainer>
            <IconContainer>
              <Icon icon={IconType.GOAL_FLAG} width={24} height={24} className="rtl-flip" />
            </IconContainer>
            <Title>{title}</Title>
          </TitleContainer>
          <DateLabelContainer cardStatus={cardStatus}>
            <StyledIconMobile width={16} height={16} icon={IconType.CALENDAR} />
            <DateLabel>{dd.toLocaleString(langCode, { month: 'short', day: 'numeric', year: 'numeric' })}</DateLabel>
            <StyledIcon width={16} height={16} icon={IconType.CALENDAR} />
          </DateLabelContainer>
        </Row>
        <Row>
          <ProgressBarContainer>
            <ProgressBar>
              <ProgressFill
                progress={progress}
                backgroundColor={
                  isCompleted
                    ? theme.colors.prototype.success
                    : cardStatus === CardStatus.urgent
                      ? theme.colors.prototype.destructive
                      : theme.colors.prototype.primary
                }
              />
            </ProgressBar>
          </ProgressBarContainer>
          <StatsContainer>
            {!hideTooltip && (
              <Tooltip
                offset={15}
                content={
                  <TooltipInfoContainer>
                    <NamedContentEntryText
                      refKey="managerTeamCyberSecurityTraining"
                      subKey="seeTrainingCompletions"
                      container="span"
                    />
                  </TooltipInfoContainer>
                }
                placement="top"
              >
                <StatsItemTrainingCompleation isClickable={!isModalOpen} onClick={handleOpenTrainingUsersModal}>
                  <Icon width={16} height={16} icon={IconType.USER_COUNT} fill="none" />
                  <span>
                    <CompletedLearnersCount>{learnersCompletedTotal}</CompletedLearnersCount> / {totalLearners}
                  </span>
                </StatsItemTrainingCompleation>
              </Tooltip>
            )}
            {hideTooltip && (
              <StatsItemTrainingCompleation isClickable={false}>
                <Icon width={16} height={16} icon={IconType.USER_COUNT} fill="none" />
                <span>
                  <CompletedLearnersCount>{learnersCompletedTotal}</CompletedLearnersCount> / {totalLearners}
                </span>
              </StatsItemTrainingCompleation>
            )}
            <StatsItem>
              <Icon width={16} height={16} icon={IconType.CLOCK} fill="none" />
              <span>{estimatedCompletionTime} min</span>
            </StatsItem>
          </StatsContainer>
          {/* Nudge button moved to TrainingCompletionModal */}
        </Row>
      </MainTrainingCard>
      {isModalOpen && (
        <TrainingCompletionModal
          campaignId={campaignId}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          statsCard={{
            title,
            dueDate,
            totalLearners,
            learnersCompletedTotal,
            estimatedCompletionTime,
          }}
        />
      )}
      {nudgeModalConfigs.isOpen && (
        <NudgeModal
          nudgeConfigs={nudgeModalConfigs}
          onSendNudge={handleSendNudge}
          isSending={isPending}
          onClose={() =>
            setNudgeModalConfigs({
              isOpen: false,
            })
          }
        />
      )}
    </>
  );
};

const StatsItem = styled.div`
  display: flex;
  gap: 6px;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
`;

const StatsItemTrainingCompleation = styled(StatsItem)<{ isClickable: boolean }>`
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
  ${({ isClickable, theme }) =>
    isClickable &&
    `
    &:hover {
      background-color: ${theme.colors.prototype.secondary};
      padding: 4px;
      border-radius: 4px;
    }
  `}
`;

const StatsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  @media (max-width: 768px) {
    justify-content: space-between;
  }
`;

const CompletedLearnersCount = styled.span`
  font-weight: 600;
`;

const StyledIcon = styled(Icon)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const StyledIconMobile = styled(Icon)`
  display: none;
  @media (max-width: 768px) {
    display: flex;
  }
`;

const TooltipInfoContainer = styled.div`
  padding: 4px;
  width: 160px;
`;

const NudgeToast = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  > svg {
    cursor: pointer;

    path {
      stroke: #fff;
    }
  }
`;
